import { useState } from "react";
import { api, apiErrorMessage } from "../../../lib/api";
import { useAdminMutation, useAdminRegistrations } from "../../../lib/adminData";
import { poolDeleteConfirm } from "../../../lib/confirmCopy";
import { moveItem } from "../../../lib/reorder";
import { runSequentially } from "../../../lib/sequence";
import type { Bundle, Division, Pool } from "../../../lib/types";
import { Badge, Button, Input, SectionCard } from "../../../components/ui";

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Divisions in the order the console shows them: by `order`, then by id for the ones that share one. */
export function orderedDivisions(divisions: Division[]): Division[] {
  return [...divisions].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/**
 * The division and pool editor (console spec §5.2): one card per division
 * with its name, cap, order and pools, and the guards that keep a delete from
 * orphaning what points at it — `deleteDivision` removes the record alone,
 * and `deletePool` unassigns entries but leaves games counting under a pool
 * that no longer exists.
 */
export function Divisions({
  tid,
  bundle,
  pendingRegistrations,
}: {
  tid: string;
  bundle: Bundle;
  pendingRegistrations: number | null;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());
  const addDivision = useAdminMutation(tid, () =>
    api.post(`/admin/tournaments/${tid}/divisions`, { name: name.trim() }),
  );
  // The queue by division, for the delete guard. Same query the shell holds
  // for the rail, so this costs no request — and it is only mounted for a
  // caller who may read it.
  const regsQ = useAdminRegistrations(tid, { enabled: pendingRegistrations !== null });
  const pendingIn = (did: string) =>
    (regsQ.data?.registrations ?? []).filter((r) => r.status === "pending" && r.divisionId === did).length;

  const divisions = orderedDivisions(bundle.divisions);

  /**
   * Move a division one place and write every order that changed. Divisions
   * are created with `order: 0`, so swapping two orders would swap nothing;
   * the whole list is renumbered from the new sequence instead.
   */
  const move = async (index: number, direction: -1 | 1) => {
    const next = moveItem(divisions, index, direction);
    const writes = next
      .map((d, order) => ({ d, order }))
      .filter(({ d, order }) => d.order !== order)
      .map(({ d, order }) => () => api.put(`/admin/tournaments/${tid}/divisions/${d.id}`, { order }));
    const r = await runSequentially(writes);
    setError(r.error ? apiErrorMessage(r.error, "Could not reorder the divisions.") : undefined);
    refresh.mutate();
  };

  return (
    <SectionCard
      title="Divisions & pools"
      badge={divisions.length > 0 ? <Badge>{divisions.length}</Badge> : undefined}
    >
      {divisions.length === 0 ? (
        <p className="text-sm text-slate-400">No divisions yet — add one below.</p>
      ) : null}
      {divisions.map((d, i) => (
        <DivisionCard
          key={d.id}
          tid={tid}
          bundle={bundle}
          division={d}
          pending={pendingIn(d.id)}
          onMoveUp={i > 0 ? () => void move(i, -1) : undefined}
          onMoveDown={i < divisions.length - 1 ? () => void move(i, 1) : undefined}
        />
      ))}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <form
        className="flex flex-wrap gap-2 border-t border-slate-100 pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) addDivision.mutate(undefined, { onSuccess: () => setName("") });
        }}
      >
        <Input
          className="max-w-64"
          placeholder="Division name (e.g. 9U)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit">Add division</Button>
      </form>
    </SectionCard>
  );
}

function DivisionCard({
  tid,
  bundle,
  division: d,
  pending,
  onMoveUp,
  onMoveDown,
}: {
  tid: string;
  bundle: Bundle;
  division: Division;
  pending: number;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const [name, setName] = useState(d.name);
  const [maxTeams, setMaxTeams] = useState(d.maxTeams === undefined ? "" : String(d.maxTeams));
  const [poolName, setPoolName] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const pools = [...bundle.pools.filter((p) => p.divisionId === d.id)].sort((a, b) => a.order - b.order);
  const entries = bundle.entries.filter((e) => e.divisionId === d.id).length;
  const games = bundle.games.filter((g) => g.divisionId === d.id).length;
  const blockedPools = pools.filter((p) => bundle.games.some((g) => g.poolId === p.id));

  const run = (p: Promise<unknown>, fallback: string) =>
    p
      .then(() => {
        setError(undefined);
        refresh.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, fallback)));

  // A blank cap sends null, which clears it (console spec §6.3).
  const save = () =>
    void run(
      api.put(`/admin/tournaments/${tid}/divisions/${d.id}`, {
        name: name.trim(),
        maxTeams: maxTeams.trim() === "" ? null : Number(maxTeams),
      }),
      "Could not save this division.",
    );
  const dirty = name.trim() !== d.name || (maxTeams.trim() === "" ? undefined : Number(maxTeams)) !== d.maxTeams;
  const capInvalid = maxTeams.trim() !== "" && !(Number(maxTeams) > 0 && Number.isInteger(Number(maxTeams)));

  const addPool = () => {
    const pn = poolName.trim();
    if (!pn) return;
    void run(
      api
        .post(`/admin/tournaments/${tid}/pools`, { divisionId: d.id, name: pn, order: pools.length })
        .then(() => setPoolName("")),
      "Could not add this pool.",
    );
  };

  // The guard: a deleted division leaves its entries, games and pending
  // registrations pointing at nothing — and a registration for a deleted
  // division would sit in the queue with an Approve that 404s.
  const removals = [entries > 0 ? "teams" : "", games > 0 ? "games" : ""].filter(Boolean);
  const clauses = [
    removals.length > 0 ? `remove its ${removals.join(" and ")}` : "",
    pending > 0 ? `decide its ${count(pending, "pending registration")}` : "",
  ].filter(Boolean);
  const deleteReason =
    clauses.length > 0 ? `${clauses.join(", and ")} first.`.replace(/^./, (c) => c.toUpperCase()) : null;

  const remove = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    void (async () => {
      const r = await runSequentially([
        ...pools.map((p) => () => api.del(`/admin/tournaments/${tid}/pools/${p.id}`)),
        () => api.del(`/admin/tournaments/${tid}/divisions/${d.id}`),
      ]);
      setError(r.error ? apiErrorMessage(r.error, "Could not delete this division.") : undefined);
      refresh.mutate();
    })();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-display font-semibold text-navy-950">{d.name}</span>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" aria-label={`Move ${d.name} up`} onClick={onMoveUp} disabled={!onMoveUp}>
            ↑
          </Button>
          <Button size="sm" variant="ghost" aria-label={`Move ${d.name} down`} onClick={onMoveDown} disabled={!onMoveDown}>
            ↓
          </Button>
          <Button
            size="sm"
            variant={confirmingDelete ? "danger" : "ghost"}
            onClick={remove}
            onBlur={() => setConfirmingDelete(false)}
            disabled={deleteReason !== null}
          >
            {confirmingDelete ? "Confirm delete" : "Delete division"}
          </Button>
        </div>
      </div>
      {deleteReason ? <p className="mt-1 text-right text-xs text-slate-500">{deleteReason}</p> : null}

      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !capInvalid && name.trim()) save();
        }}
      >
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">Name</span>
          <Input uiSize="sm" className="w-40" value={name} onChange={(e) => setName(e.target.value)} aria-label={`${d.name} name`} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">Max teams</span>
          <Input
            uiSize="sm"
            inputMode="numeric"
            className="w-24"
            placeholder="No cap"
            value={maxTeams}
            onChange={(e) => setMaxTeams(e.target.value)}
            aria-label={`${d.name} max teams`}
          />
        </label>
        <Button type="submit" size="sm" variant="secondary" disabled={!dirty || capInvalid || !name.trim()}>
          Save
        </Button>
        <span className="text-xs text-slate-500">
          {count(entries, "team")}
          {d.maxTeams !== undefined ? ` of ${d.maxTeams}` : ""}
        </span>
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-slate-600">Pools:</span>
        {pools.length === 0 ? <span className="text-xs text-slate-400">No pools yet.</span> : null}
        {pools.map((p) => (
          <PoolChip key={p.id} tid={tid} bundle={bundle} pool={p} onError={setError} />
        ))}
        {blockedPools.length > 0 ? (
          <span className="text-xs text-slate-500">
            {blockedPools.map((p) => p.name).join(" and ")} {blockedPools.length === 1 ? "has" : "have"} games —
            delete its games under Schedule first.
          </span>
        ) : null}
        <form
          className="ml-auto flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addPool();
          }}
        >
          <Input
            uiSize="sm"
            className="max-w-44"
            placeholder="Pool name"
            value={poolName}
            onChange={(e) => setPoolName(e.target.value)}
            aria-label={`New pool in ${d.name}`}
          />
          <Button type="submit" variant="secondary" size="sm">
            Add pool
          </Button>
        </form>
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

/**
 * One pool as a chip: its name, a pencil that turns the chip into an inline
 * rename, and a cross that deletes it — held shut while any game is still
 * filed under it, because those games would keep counting toward a pool
 * that no longer exists.
 */
function PoolChip({
  tid,
  bundle,
  pool,
  onError,
}: {
  tid: string;
  bundle: Bundle;
  pool: Pool;
  onError: (message: string | undefined) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pool.name);
  const [confirming, setConfirming] = useState(false);
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const entries = bundle.entries.filter((e) => e.poolId === pool.id).length;
  const games = bundle.games.filter((g) => g.poolId === pool.id).length;

  const run = (p: Promise<unknown>, fallback: string) =>
    p
      .then(() => {
        onError(undefined);
        refresh.mutate();
      })
      .catch((e) => onError(apiErrorMessage(e, fallback)));

  const rename = () => {
    setEditing(false);
    const next = name.trim();
    if (!next || next === pool.name) {
      setName(pool.name);
      return;
    }
    void run(api.put(`/admin/tournaments/${tid}/pools/${pool.id}`, { name: next }), "Could not rename this pool.");
  };

  const remove = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    void run(api.del(`/admin/tournaments/${tid}/pools/${pool.id}`), "Could not delete this pool.");
  };

  if (editing)
    return (
      <Input
        uiSize="sm"
        className="w-32"
        autoFocus
        value={name}
        aria-label={`Rename ${pool.name}`}
        onChange={(e) => setName(e.target.value)}
        onBlur={rename}
        onKeyDown={(e) => {
          if (e.key === "Enter") rename();
          if (e.key === "Escape") {
            setName(pool.name);
            setEditing(false);
          }
        }}
      />
    );

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-inset ring-slate-500/10">
      {pool.name}
      <button
        type="button"
        aria-label={`Rename ${pool.name}`}
        onClick={() => setEditing(true)}
        className="rounded-full px-1 text-slate-400 hover:text-navy-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
      >
        ✎
      </button>
      {games > 0 ? (
        // Held shut, not hidden: the chip keeps its shape, and the reason is
        // the note after the row.
        <span aria-hidden="true" className="px-1 text-slate-300">
          ×
        </span>
      ) : (
        <button
          type="button"
          aria-label={confirming ? poolDeleteConfirm(entries) : `Delete ${pool.name}`}
          onClick={remove}
          onBlur={() => setConfirming(false)}
          className={
            confirming
              ? "rounded-full bg-red-50 px-1.5 normal-case tracking-normal text-red-600 ring-1 ring-red-200"
              : "rounded-full px-1 text-slate-400 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
          }
        >
          {confirming ? poolDeleteConfirm(entries) : "×"}
        </button>
      )}
    </span>
  );
}
